import React, { useMemo } from 'react';
import {
  Box,
  Icon,
  InlineMessage,
  RadioButton,
  RadioButtonGroup,
  type RadioButtonRadioSelection,
} from '@saviynt/design-system';
import cx from 'classnames';

import { FieldLabel } from '../../../../shared/components';
import { testIds, VALIDATION_TYPES } from '../../../../shared/constants';
import {
  type DataProvider,
  type ProviderOption,
  type RadioField,
} from '../../../../shared/types';

import styles from './PreviewFields.module.css';

interface RenderRadioOption {
  value: string;
  label: string;
  isChecked?: boolean;
}

const toBool = (v: unknown): boolean | undefined => {
  if (typeof v === 'boolean') {
    return v;
  }

  if (typeof v === 'string') {
    return v.toLowerCase() === 'true';
  }

  return undefined;
};

const mapProviderOption = (o: ProviderOption): RenderRadioOption => ({
  value: String(o.value),
  label: o.label ?? String(o.value),
  isChecked: toBool(o.isChecked),
});

export interface RadioFieldProps {
  field: RadioField;
  radioSelection: string | null;
  onChange: (value: string) => void;
  error?: string;
  isRequired?: boolean;
  isReadOnly?: boolean;
  dataProvider?: DataProvider;
}

function Radio({
  field,
  radioSelection,
  onChange,
  error,
  isRequired: isRequiredProp,
  isReadOnly: isReadOnlyProp,
  dataProvider,
}: RadioFieldProps) {
  const {
    id,
    fieldName,
    label,
    layout,
    helperText,
    infoTooltipText,
    validations,
  } = field;

  // Use prop if provided, otherwise fallback to validation rules
  const isRequired =
    isRequiredProp ??
    validations?.some((rule) => rule.type === VALIDATION_TYPES.REQUIRED) ??
    false;

  const isReadOnly = field.isReadOnly || isReadOnlyProp || false;

  const options: RenderRadioOption[] = useMemo(() => {
    const { fixed } = (dataProvider as { fixed?: ProviderOption[] }) || {};

    return Array.isArray(fixed) ? fixed.map(mapProviderOption) : [];
  }, [dataProvider]);

  if (options.length === 0) {
    return null;
  }

  // Default selection should use the option's LABEL
  const defaultChecked = options.find((o) => o.isChecked)?.label ?? null;
  const selectedValue =
    radioSelection !== null && radioSelection !== undefined
      ? radioSelection
      : defaultChecked;

  const shouldRequiredAsterisk =
    isRequired && (selectedValue === null || selectedValue === undefined);

  const groupClasses = cx(
    styles.radioGroup,
    layout === 'vertical' && styles.radioGroup__isVertical,
    layout === 'horizontal' && styles.radioGroup__isHorizontal
  );

  const groupBottomText = error ?? helperText;

  return (
    <Box className={styles.radio}>
      <Box className='FF-Radio-header'>
        <FieldLabel
          label={label}
          isRequired={isRequired}
          isCritical={!!error}
          isReadOnly={isReadOnly}
          infoTooltipText={infoTooltipText}
          shouldRequiredAsterisk={shouldRequiredAsterisk}
        />
      </Box>
      <RadioButtonGroup
        name={fieldName}
        value={selectedValue as RadioButtonRadioSelection}
        onChange={(val) => onChange(String(val))}
        orientation={layout}
        ariaLabel={label}
        className={groupClasses}
        dataTestId={testIds.FF_PREVIEW_RADIO_FIELD}>
        {options.map((option) => (
          <RadioButton
            key={`${id}-${option.value}`}
            id={`${id}-${option.value}`}
            kind='singleLine'
            name={fieldName}
            title={option.label || '-'}
            titleLineLimit={4}
            value={option.label}
            radioSelection={selectedValue as RadioButtonRadioSelection}
            isDisabled={isReadOnly}
            onClick={() => onChange(option.label)}
            className={styles.radioItem}
            classNames={{
              titleClassName: cx(isReadOnly && styles.value__isReadOnly),
            }}
          />
        ))}
      </RadioButtonGroup>
      {(error || helperText) && (
        <InlineMessage
          text={groupBottomText}
          colorTheme={error ? 'critical' : 'neutral'}
          size='small'
          leftIcon={error ? <Icon kind='AlertCriticalFilled' /> : undefined}
        />
      )}
    </Box>
  );
}

export default Radio;
